import { Box, Fade } from '@mui/material'
import cn from 'classnames'
import LottieSuccessIcon from 'ext-ui/components/Dropdown/components/LottieSuccessIcon/LottieSuccessIcon'
import Processing from 'ext-ui/components/Processing/Processing'
import TextInputInternal from 'ext-ui/components/TextInput/TextInputInternal/TextInputInternal'
import TooltipIcon, {
  TooltipIconProps,
} from 'ext-ui/components/TooltipIcon/TooltipIcon'
import {
  LoadingState,
  useLoadingStateAnimation,
} from 'ext-ui/hooks/useLoadingStateAnimation'
import React, { useId, useImperativeHandle, useRef } from 'react'
import { useTranslate } from 'src/localization/Trns'

import style from './TextInput.style'

const renderErrors = (error: React.ReactNode): React.ReactNode => {
  if (!error) return null

  if (Array.isArray(error)) {
    return error.map((err, index) => <div key={index}>{err}</div>)
  }

  return <div>{error}</div>
}

export type TextInputVariant = 'medium' | 'small'
export type TextInputState = 'default' | 'hovered' | 'focused' | 'error'

export type TextInputProps = {
  className?: string
  /**
   * Style override for the bordered input control that contains the icons and
   * input value.
   */
  containerStyle?: React.CSSProperties
  /** Style override for the input value. */
  style?: React.CSSProperties
  variant?: TextInputVariant
  /**
   * style override for the input, if undefined, the input will base its style
   * on its own internal input state
   */
  state?: TextInputState
  label?: string
  /**
   * if 'optional' will show "(Optional)" text at the end of the label
   * if 'required' will show a red asterisk (*) immediately after the label
   * if undefined, neither will be shown
   */
  requirement?: 'optional' | 'required'
  /**
   * will render a TooltipIcon next to the label
   */
  tooltipProps?: TooltipIconProps
  /**
   * custom tooltip element to render next to the label
   */
  tooltip?: React.ReactElement
  tooltipPosition?: 'left' | 'right'
  microcopy?: string
  placeholder?: string
  loadingState?: LoadingState
  disabled?: boolean
  errorText?: React.ReactNode
  iconLeft?: React.ReactElement
  iconRight?: React.ReactElement
  /** Interactive leading content inside the field chrome (e.g. a currency selector).
   *  Rendered inside the bordered container but OUTSIDE the <label>, so it never
   *  captures the input's label association. */
  leadingSlot?: React.ReactNode
  /** Interactive trailing content inside the field chrome (e.g. a calendar toggle). */
  trailingSlot?: React.ReactNode
  baseValue?: string
  testId?: string
  labelTestId?: string
  value?: string
}

type TextInputComponentProps = React.ComponentProps<'input'> & TextInputProps

export interface TextInputRef {
  focus: () => void
  container: HTMLDivElement | null
  input: HTMLInputElement | null
}

const TextInput = React.forwardRef<TextInputRef, TextInputComponentProps>(
  (props, ref): React.ReactElement => {
    const {
      className = '',
      containerStyle,
      variant = 'medium',
      state,
      label,
      requirement,
      tooltipPosition = 'right',
      tooltip,
      tooltipProps,
      microcopy,
      errorText,
      iconLeft,
      iconRight,
      leadingSlot,
      trailingSlot,
      baseValue,
      loadingState,
      disabled,
      testId,
      labelTestId,
      id,
      value,
      ...rest
    } = props

    const reactId = useId()
    const errorId = `${reactId}-error`
    const microcopyId = `${reactId}-microcopy`
    const optionalId = `${reactId}-optional`
    const baseValueId = `${reactId}-base-value`
    const inputId = id ?? `${reactId}-input`
    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => ({
      focus: (options?: FocusOptions) => {
        inputRef.current?.focus(options)
      },
      get container() {
        return containerRef.current
      },
      get input() {
        return inputRef.current
      },
    }))

    const { showIdle, showPending, showSuccess } =
      useLoadingStateAnimation(loadingState)

    // Everything rendered about this field, in reading order, merged with
    // whatever the consumer already pointed at. Spreading `rest` cannot reach
    // this: the attribute is written after it, so a consumer's own
    // aria-describedby used to be dropped on the floor.
    const describedBy =
      [
        rest['aria-describedby'],
        requirement === 'optional' ? optionalId : undefined,
        baseValue ? baseValueId : undefined,
        microcopy ? microcopyId : undefined,
        errorText ? errorId : undefined,
      ]
        .filter(Boolean)
        .join(' ') || undefined

    const translate = useTranslate()

    const handleContainerClick = (e: React.MouseEvent): void => {
      const target = e.target as HTMLElement
      const input = inputRef.current
      if (!input || target === input) return
      if (target.closest('button, a, input, select, textarea, [role="button"]'))
        return
      input.focus()
      input.click()
    }

    return (
      <div
        className={cn(className, variant)}
        css={style}
        data-testid={
          testId
            ? `${testId}-group`
            : label
              ? `${label.toLowerCase().replace(/\s+/g, '-')}-group`
              : ''
        }
        data-label={label}
        data-value={value}
      >
        <div
          className={cn('label', state, variant, {
            error: errorText || state === 'error' || loadingState === 'error',
          })}
        >
          {label && (
            <div className={'label-field'}>
              {tooltipPosition === 'left' && tooltip}
              {tooltipPosition === 'left' && tooltipProps && (
                <TooltipIcon {...tooltipProps} />
              )}
              <label
                htmlFor={inputId}
                data-testid={labelTestId ?? `${testId}-labelField`}
              >
                {label}
              </label>
              {requirement === 'required' && (
                // Decorative: `aria-required` on the input is the half
                // assistive tech reads, and this would otherwise be announced
                // as a bare "star" after the label.
                <span className={'required-asterisk'} aria-hidden={'true'}>
                  {'*'}
                </span>
              )}
              {baseValue && (
                <span id={baseValueId}>
                  {translate('(was "${baseValue}")', {
                    baseValue,
                  })}
                </span>
              )}
              {tooltipPosition === 'right' && tooltip}
              {tooltipPosition === 'right' && tooltipProps && (
                <TooltipIcon {...tooltipProps} />
              )}
              {requirement === 'optional' && (
                <span id={optionalId}>{translate('(Optional)')}</span>
              )}
            </div>
          )}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- forwards padding clicks to the real control (the <input>), which stays natively keyboard-reachable via Tab and the <label> above */}
          <div
            data-testid={
              testId
                ? `${testId}-textbox`
                : label
                  ? `${label.toLowerCase().replace(/\s+/g, '-')}-textbox`
                  : ''
            }
            ref={containerRef}
            style={containerStyle}
            className={cn('textbox-container', variant, {
              disabled,
            })}
            onClick={handleContainerClick}
          >
            {leadingSlot && <div className="leading-slot">{leadingSlot}</div>}
            {iconLeft && (
              <div className={cn('textbox-icon', variant)}>{iconLeft}</div>
            )}
            <TextInputInternal
              variant={variant}
              disabled={disabled}
              value={value}
              {...rest}
              ref={inputRef}
              id={inputId}
              aria-invalid={!!errorText || state === 'error' || undefined}
              aria-describedby={describedBy}
              aria-required={requirement === 'required' || undefined}
              data-testid={
                testId
                  ? `${testId}-input`
                  : label
                    ? `${label.toLowerCase().replace(/\s+/g, '-')}-input`
                    : ''
              }
            />
            {trailingSlot && (
              <div className="trailing-slot">{trailingSlot}</div>
            )}
            {loadingState ? (
              <div
                className={cn(
                  'textbox-icon',
                  'self-end',
                  'animation-container',
                  variant,
                )}
              >
                <Fade in={showIdle} timeout={350} unmountOnExit>
                  <Box className="animation-icon-container">{iconRight}</Box>
                </Fade>
                <Fade in={showPending} timeout={350} unmountOnExit>
                  <Box className="animation-icon-container">
                    <Processing width={16} height={16} viewBox={'0 0 24 24'} />
                  </Box>
                </Fade>
                <Fade in={showSuccess} timeout={350} unmountOnExit>
                  <Box className="animation-icon-container">
                    <LottieSuccessIcon play={showSuccess} freezeOnLastFrame />
                  </Box>
                </Fade>
              </div>
            ) : (
              iconRight && (
                <div className={cn('textbox-icon', 'self-end', variant)}>
                  {iconRight}
                </div>
              )
            )}
          </div>
        </div>
        {microcopy && (
          <div id={microcopyId} className={cn('helper-text', variant)}>
            {microcopy}
          </div>
        )}
        {errorText && (
          <div id={errorId} role="alert" className={cn('error-text', variant)}>
            {renderErrors(errorText)}
          </div>
        )}
      </div>
    )
  },
)

export default TextInput
